import { useEffect, useMemo, useState } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { subscribeShared } from '../lib/sharedQuery';

export interface StockRow {
  id: string;
  productId: string;
  productCode: string;
  productName: string;
  warehouseId: string;
  warehouseName: string;
  locationId?: string;
  locationName?: string;
  quantity: number;
  reservedQuantity: number;
  minQuantity?: number;
  unit: string;
  lastUpdated?: Date;
}

export interface StockProduct {
  id: string;
  code: string;
  name: string;
  unit: string;
  barcode?: string;
  purchasePrice?: number;
  netPrice?: number;
  minStock?: number;
  optimalStock?: number;
  maxStock?: number;
  manufacturer?: string;
  supplierCode?: string;
  type?: string;
  isActive?: boolean;
  category?: string;
}

/** Egy termék összesített készlete raktárbontással. */
export interface StockLine {
  productId: string;
  product?: StockProduct;
  code: string;
  name: string;
  unit: string;
  total: number;
  reserved: number;
  available: number;
  min?: number;
  value: number;          // total × beszerzési ár
  status: 'ok' | 'low' | 'out' | 'negative';
  rows: StockRow[];       // raktáronként
  lastUpdated?: Date;
}

const toDate = (v: any): Date | undefined => (v?.toDate ? v.toDate() : v instanceof Date ? v : v ? new Date(v) : undefined);

/**
 * Realtime készlet: inventory sorok + termékek (beszerzési ár, min. készlet, vonalkód) egy megosztott figyelővel.
 * Termékenként összesít; a minimum: termék minStock, ha nincs, a raktársorok minQuantity összege.
 */
export function useStock() {
  const { user } = useAuth();
  const orgId = (user as any)?.organizationId as string | undefined;
  const [rows, setRows] = useState<StockRow[]>([]);
  const [products, setProducts] = useState<StockProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) { setRows([]); setLoading(false); return; }
    const unsub = subscribeShared<StockRow>(
      `inventory:${orgId}`,
      () => query(collection(db, 'inventory'), where('organizationId', '==', orgId)),
      snap => snap.docs.map(d => {
        const x = d.data() as any;
        return {
          id: d.id, productId: x.productId, productCode: x.productCode || '', productName: x.productName || '',
          warehouseId: x.warehouseId, warehouseName: x.warehouseName || '', locationId: x.locationId, locationName: x.locationName,
          quantity: Number(x.quantity || 0), reservedQuantity: Number(x.reservedQuantity || 0),
          minQuantity: x.minQuantity != null ? Number(x.minQuantity) : undefined, unit: x.unit || 'db', lastUpdated: toDate(x.lastUpdated)
        };
      }),
      (data) => { setRows(data || []); setLoading(false); }
    );
    return () => unsub();
  }, [orgId]);

  useEffect(() => {
    if (!orgId) { setProducts([]); return; }
    const unsub = subscribeShared<StockProduct>(
      `stockProducts:${orgId}`,
      () => query(collection(db, 'products'), where('organizationId', '==', orgId)),
      snap => snap.docs.map(d => {
        const x = d.data() as any;
        return {
          id: d.id, code: x.code || '', name: x.name || '', unit: x.unit || 'db', barcode: x.barcode || undefined,
          purchasePrice: x.purchasePrice != null ? Number(x.purchasePrice) : undefined,
          netPrice: x.netPrice != null ? Number(x.netPrice) : undefined,
          minStock: x.minStock != null && x.minStock !== '' ? Number(x.minStock) : undefined,
          optimalStock: x.optimalStock != null && x.optimalStock !== '' ? Number(x.optimalStock) : undefined,
          maxStock: x.maxStock != null && x.maxStock !== '' ? Number(x.maxStock) : undefined,
          manufacturer: x.manufacturer || undefined, supplierCode: x.supplierCode || undefined,
          type: x.type, isActive: x.isActive !== false, category: x.category?.name || x.category || undefined
        };
      }),
      (data) => setProducts(data || [])
    );
    return () => unsub();
  }, [orgId]);

  const lines = useMemo<StockLine[]>(() => {
    const pmap = new Map(products.map(p => [p.id, p]));
    const byProduct = new Map<string, StockRow[]>();
    rows.forEach(r => { if (!byProduct.has(r.productId)) byProduct.set(r.productId, []); byProduct.get(r.productId)!.push(r); });
    const out: StockLine[] = [];
    byProduct.forEach((rs, pid) => {
      const p = pmap.get(pid);
      const total = rs.reduce((s, r) => s + r.quantity, 0);
      const reserved = rs.reduce((s, r) => s + r.reservedQuantity, 0);
      const rowMins = rs.filter(r => r.minQuantity != null).map(r => r.minQuantity!);
      const min = p?.minStock != null ? p.minStock : rowMins.length ? rowMins.reduce((a, b) => a + b, 0) : undefined;
      const status: StockLine['status'] = total < 0 ? 'negative' : total === 0 ? 'out' : min != null && total < min ? 'low' : 'ok';
      const last = rs.map(r => r.lastUpdated?.getTime() || 0).reduce((a, b) => Math.max(a, b), 0);
      out.push({
        productId: pid, product: p, code: p?.code || rs[0].productCode, name: p?.name || rs[0].productName, unit: p?.unit || rs[0].unit,
        total: Math.round(total * 1000) / 1000, reserved, available: total - reserved, min,
        value: total * (p?.purchasePrice || 0), status, rows: rs.sort((a, b) => b.quantity - a.quantity), lastUpdated: last ? new Date(last) : undefined
      });
    });
    // Termékek, amelyeknek van minimuma, de még nincs készletsoruk → „nincs készleten”
    products.forEach(p => {
      if (byProduct.has(p.id) || p.type === 'service' || p.isActive === false) return;
      if (p.minStock != null && p.minStock > 0) out.push({ productId: p.id, product: p, code: p.code, name: p.name, unit: p.unit, total: 0, reserved: 0, available: 0, min: p.minStock, value: 0, status: 'out', rows: [] });
    });
    return out.sort((a, b) => a.name.localeCompare(b.name, 'hu'));
  }, [rows, products]);

  const stats = useMemo(() => ({
    items: lines.filter(l => l.total !== 0).length,
    value: lines.reduce((s, l) => s + Math.max(0, l.value), 0),
    low: lines.filter(l => l.status === 'low').length,
    out: lines.filter(l => l.status === 'out' || l.status === 'negative').length,
    reserved: lines.filter(l => l.reserved > 0).length,
    missingPrice: lines.filter(l => l.total > 0 && !(l.product?.purchasePrice! > 0)).length
  }), [lines]);

  return { rows, products, lines, stats, loading };
}
